import React from 'react'
import {
  Br,
  Cut,
  Line,
  Printer,
  render,
  Row,
  Text,
} from 'react-thermal-printer'
import { formatPrice } from '@/lib/utils'

interface TransactionSummary {
  payment_method: string
  transaction_total: number
  transaction_count: number
}

interface FullSummary {
  order_items: {
    order_items_with_addons: any[]
    order_items_without_addons: any[]
    sold_as_addons: any[]
  }
  register_opening_balance: number
  order_totals_summary: any
}

interface PrinterSummary {
  summary: TransactionSummary[]
  fullSummary: FullSummary
  closingBalance: number
  location?: string
}

interface PrinterEndpoint {
  direction: string
  endpointNumber: number
}

interface USBPrinter extends USBDevice {
  configuration: USBConfiguration & {
    interfaces: {
      alternate: {
        endpoints: PrinterEndpoint[]
      }
    }[]
  }
}

export const useCloseRegisterPrinter = () => {
  const [printer, setPrinter] = React.useState<USBPrinter | null>(null)

  const requestPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const device = (await navigator.usb.requestDevice({
        filters: [
          {
            vendorId: 0x0456, // Vendor ID for the printer
          },
        ],
      })) as USBPrinter
      setPrinter(device)
      return device
    } catch (e) {
      console.error('Error connecting to device:', e)
      return null
    }
  }

  const getPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const devices = await navigator.usb.getDevices()
      if (devices.length > 0) {
        const device = devices[0] as USBPrinter
        setPrinter(device)
        return device
      }
      return null
    } catch (e) {
      console.error('Error getting devices:', e)
      return null
    }
  }

  const template = ({
    summary,
    fullSummary,
    closingBalance,
    location,
  }: PrinterSummary) => {
    // Early return if required data is missing
    if (!summary && !fullSummary) return null

    return (
      <Printer type="star" width={48}>
        <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
          Close Register Report
        </Text>
        <Line />
        <Row
          left={<Text bold={true}>Opening Balance</Text>}
          right={
            <Text bold={true}>
              {formatPrice(fullSummary?.register_opening_balance, 2, false)}
            </Text>
          }
        />
        <Row
          left={<Text bold={true}>Closing Balance</Text>}
          right={
            <Text bold={true}>{formatPrice(closingBalance, 2, false)}</Text>
          }
        />
        <Line />

        {Array.isArray(summary) &&
          summary.map((item, index) => (
            <Row
              key={`summary-${index}`}
              left={
                <Text>
                  #{index + 1} - {item.payment_method} /{' '}
                  {item.transaction_count} Txs
                </Text>
              }
              right={
                <Text>
                  {formatPrice(Number(item.transaction_total), 2, false)}
                </Text>
              }
              gap={5}
            />
          ))}

        <Line />

        <Text bold={true}>Order Items with Addons</Text>

        <Line />

        {fullSummary?.order_items?.order_items_with_addons?.map(
          (item, index) => (
            <Row
              key={`full-${index}`}
              left={
                <Text>
                  #{index + 1} - {item.product_name} /{' '}
                  {item.total_item_sold_qty}(Qty) / {item.total_addons_sold_qty}
                  (Addons)
                </Text>
              }
              right={
                <Text>
                  {formatPrice(Number(item.total_item_sold_price), 2, false)}
                </Text>
              }
              gap={5}
            />
          ),
        )}

        <Line />

        <Text bold={true}>Order Items without Addons</Text>

        <Line />

        {fullSummary?.order_items?.order_items_without_addons?.map(
          (item, index) => (
            <Row
              key={`full-${index}`}
              left={
                <Text>
                  #{index + 1} - {item.product_name} /{' '}
                  {item.total_item_sold_qty}(Qty)
                </Text>
              }
              right={
                <Text>
                  {formatPrice(Number(item.total_item_sold_price), 2, false)}
                </Text>
              }
              gap={5}
            />
          ),
        )}

        <Line />

        <Text bold={true}>Sold as Addons</Text>

        <Line />

        {fullSummary?.order_items?.sold_as_addons?.map((item, index) => (
          <Row
            key={`full-${index}`}
            left={
              <Text>
                #{index + 1} - {item.product_name} /{' '}
                {item.total_addons_sold_qty}
                (Qty)
              </Text>
            }
            right={
              <Text>
                {formatPrice(Number(item.total_item_sold_price), 2, false)}
              </Text>
            }
            gap={5}
          />
        ))}

        <Line />

        <Text bold={true}>Summary</Text>

        <Line />

        {fullSummary?.order_totals_summary?.map((item, index) => (
          <Row
            key={`full-${index}`}
            left={<Text>{item?.total_name}</Text>}
            right={
              <Text>{formatPrice(Number(item?.total_amount), 2, false)}</Text>
            }
            gap={5}
          />
        ))}

        <Line />
        <Text align="center">System by et.lk</Text>
        <Cut />
      </Printer>
    )
  }

  const printReceipt = async (summary: PrinterSummary): Promise<void> => {
    try {
      let currentPrinter = await getPrinter()

      if (!currentPrinter) {
        currentPrinter = await requestPrinter()
        if (!currentPrinter) {
          throw new Error('No printer available')
        }
      }

      // Render template in a separate operation
      const printData: Uint8Array = await new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const templateResult = template(summary)
            if (!templateResult) {
              reject(new Error('Failed to generate template'))
              return
            }
            const rendered = await render(templateResult)
            resolve(rendered)
          } catch (error) {
            reject(error)
          }
        }, 0)
      })

      await currentPrinter.open()
      await currentPrinter.selectConfiguration(1)
      await currentPrinter.claimInterface(0)

      const endpoint =
        currentPrinter.configuration.interfaces[0].alternate.endpoints.find(
          (ep: PrinterEndpoint) => ep.direction === 'out',
        )

      if (!endpoint) {
        throw new Error('No valid endpoint found')
      }

      await currentPrinter.transferOut(endpoint.endpointNumber, printData)
      await currentPrinter.close()
    } catch (e) {
      console.error('Error printing receipt:', e)
      throw e
    }
  }

  return {
    printer,
    requestPrinter,
    getPrinter,
    template,
    printReceipt,
  }
}
